import { BrokenInAppBrowser } from "./detect";

/**
 * A working hand-off takes ~1.2s to show up as a hidden webview (measured, iOS
 * 26.5.2). Firing sooner would credit the wrong scheme in telemetry.
 */
export const ESCAPE_FIRST_ATTEMPT_INTERVAL_MS = 2_000;

/** Shorter for the long shots, so the spinner does not drag. */
export const ESCAPE_ATTEMPT_INTERVAL_MS = 1_000;

/** Grace after the last scheme before declaring the chain failed. */
export const ESCAPE_FINAL_SETTLE_MS = 1_500;

/**
 * Shared with the server-rendered escape page, which bakes these numbers into
 * its inline script, so the two cannot drift apart.
 */
export function escapeAttemptDelayMs(index: number, total: number): number {
  if (index === total - 1) return ESCAPE_FINAL_SETTLE_MS;
  if (index === 0) return ESCAPE_FIRST_ATTEMPT_INTERVAL_MS;
  return ESCAPE_ATTEMPT_INTERVAL_MS;
}

/** Also the telemetry endpoint's allowlist, so no unknown value is stored. */
export const ESCAPE_SCHEMES = [
  "itms-apps",
  "extbrowser",
  "x-safari",
  "googlechromes",
] as const;

export type EscapeScheme = (typeof ESCAPE_SCHEMES)[number];

export type EscapeAttempt = {
  url: string;
  /** Telemetry label rather than the URL, which would put link data in analytics. */
  scheme: EscapeScheme;
};

const APP_STORE_URL_PATTERN = /^https:\/\/apps\.apple\.com\//i;
const HTTPS_URL_PATTERN = /^https:\/\//i;

/**
 * The gate on the whole workaround. Only App Store URLs break, so escaping
 * anything else would throw a visitor out of the host app for nothing.
 */
export function isBlockedDestination(destination: string): boolean {
  return APP_STORE_URL_PATTERN.test(destination);
}

/**
 * Hands a URL to the external browser of Instagram or Threads. Each app only
 * handles its own scheme: Threads given instagram:// asks to open the Instagram
 * app instead. Both tested on a device on 2026-10-09.
 */
export function buildExtbrowserUrl(
  browser: BrokenInAppBrowser,
  url: string
): string {
  const appScheme = browser === "threads" ? "barcelona" : "instagram";
  return `${appScheme}://extbrowser/?url=${encodeURIComponent(url)}`;
}

export const xSafariAttempt = (url: string): EscapeAttempt => ({
  url: `x-safari-${url}`,
  scheme: "x-safari",
});

const itmsAppsAttempt = (storeUrl: string): EscapeAttempt => ({
  url: storeUrl.replace(/^https:/i, "itms-apps:"),
  scheme: "itms-apps",
});

const googleChromeAttempt = (url: string): EscapeAttempt => ({
  url: url.replace(HTTPS_URL_PATTERN, "googlechromes://"),
  scheme: "googlechromes",
});

const extbrowserAttempt = (
  browser: BrokenInAppBrowser,
  url: string
): EscapeAttempt => ({
  url: buildExtbrowserUrl(browser, url),
  scheme: "extbrowser",
});

const STORE_ESCAPE_PLANS: Record<
  BrokenInAppBrowser,
  (storeUrl: string) => EscapeAttempt[]
> = {
  // The webview blocks the extbrowser scheme. Its taps skip this chain, since a
  // tapped store link opens there (see opensStoreLinksOnTap).
  facebook: (storeUrl) => [
    xSafariAttempt(storeUrl),
    itmsAppsAttempt(storeUrl),
    googleChromeAttempt(storeUrl),
  ],
  // The external browser then bounces into the App Store on its own.
  instagram: (storeUrl) => [
    extbrowserAttempt("instagram", storeUrl),
    itmsAppsAttempt(storeUrl),
    xSafariAttempt(storeUrl),
    googleChromeAttempt(storeUrl),
  ],
  threads: (storeUrl) => [
    extbrowserAttempt("threads", storeUrl),
    itmsAppsAttempt(storeUrl),
    xSafariAttempt(storeUrl),
    googleChromeAttempt(storeUrl),
  ],
  // Every way out is refused, see cannotEscape.
  tiktok: () => [],
};

/**
 * The schemes to try, in order, to get from a store URL these webviews refuse
 * to the store. All undocumented and all patchable by the vendor, so every
 * caller has to stay usable when the whole list fails.
 *
 * `extbrowser` leads because it is the only one measured to work. `itms-apps`
 * stayed inert through a six-second window, so Meta blocks the App Store as a
 * destination rather than the apps.apple.com host, but it is kept for the day
 * Meta patches their own scheme.
 *
 * Only for URLs that pass isBlockedDestination: handing the App Store app a
 * web URL through itms-apps just dead-ends it. The Facebook bounce page has its
 * own chain, see buildAppBounceAttempts.
 *
 * Kept free of imports because /api/link/continue serialises the result into an
 * inline script.
 */
export function buildEscapeAttempts(
  browser: BrokenInAppBrowser,
  storeUrl: string
): EscapeAttempt[] {
  return STORE_ESCAPE_PLANS[browser](storeUrl);
}
